import React, { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import { SpaceTypes } from "../../Constants/Interfaces";
import TagsInputList from "../TagsInputList";
import Tabs from "../Tabs";
import {
  amenitiesTypes,
  defaultAvailability,
  featuresTypes,
  occassionTypes,
  otherTypes,
  spaceTypes,
  vibesTypes,
} from "../../Constants";
import {
  createSpaceAction,
  updateSpaceAction,
} from "../../Services/Store/Actions/SpaceActions";
import { useDispatch } from "react-redux";
import SpaceAvailability from "../../Pages/OnBoarding/SpaceAvailability";
import SpaceInfoStepFour from "../../Pages/OnBoarding/SpaceInfoStepFour";
import SpaceInfoStepFive from "../../Pages/OnBoarding/SpaceInfoStepFive";
import { formatArea, showToast } from "../../utils/helpers";
import CustomButton from "../Button";
import { API } from "../../utils/Apis";
import useUnsavedChanges from "../../Hooks/useUnsavedChanges";
import ConfirmationModal from "../ConfirmationModal";
import AccordianDataContent from "../AccordianDataContent";
import { updateVenueSpace } from "../../Services/Store/Reducers/SpaceReducers";

interface AddVenueSpaceType {
  venueId: string;
  spaceItem: { id: string };
  show: boolean;
  showActionBtn?: boolean;
  closeModal: Function;
  // Fired once when a NEW space is created (not on edit), so the parent can
  // update its venue space count without reloading the whole list.
  onSpaceCreated?: (() => void) | null;
}

const initSpace = {
  venueId: "",
  name: "",
  tagLine: "",
  roomNumber: "",
  email: "",
  phone: "",
  capacity: "",
  area: "",
  status: "draft", // draft || published
  description: "",
  ratePerHour: "",
  minHours: "",
  discountHours: "",
  media: [],
  eventType: [],
  vibeTags: [],
  occassionTags: [],
  features: [],
  amenities: [],
  otherTags: [],
  availabilities: [],
};

const AddVenueSpace: React.FC<AddVenueSpaceType> = ({
  show,
  showActionBtn = true,
  venueId,
  spaceItem,
  closeModal,
  onSpaceCreated = null,
}) => {
  const tabs = [
    "General",
    "Features",
    "About",
    "Health & Safety",
    "Policy",
    "Schedule",
    "Price",
    "Images",
  ];

  const maxContentLimit = 1050;

  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SpaceTypes>({ ...initSpace });
  const [selectedTab, setSelectedTab] = useState("General");
  const [spaceId, setSpaceId] = useState("");
  const [remainingLimit, setRemaningLimit] = useState(maxContentLimit);

  // Sequential tab navigation for the multi-tab editor.
  const currentTabIndex = tabs.indexOf(selectedTab);
  const isFirstTab = currentTabIndex <= 0;
  const isLastTab = currentTabIndex === tabs.length - 1;

  const goToPrevTab = () => {
    if (currentTabIndex > 0) {
      setSelectedTab(tabs[currentTabIndex - 1]);
    }
  };

  const goToNextTab = () => {
    if (currentTabIndex < tabs.length - 1) {
      setSelectedTab(tabs[currentTabIndex + 1]);
    }
  };

  const [errors, setErrors] = useState({
    name: "",
    roomNumber: "",
    capacity: "",
    area: "",
    eventType: "",
    vibeTags: "",
    occassionTags: "",
    featuresTags: "",
    amenitiesTags: "",
    otherTags: "",
    description: "",
    ratePerHour: "",
    minHours: "",
    discountHours: "",
  });

  const guard = useUnsavedChanges(data, () => closeModal());

  const onChange = (field: string, value: any) => {
    const contentLength = value.length;

    if (field === "description" && contentLength > maxContentLimit) {
      return;
    }

    setData({
      ...data,
      [field]: value,
    });
  };

  const validate = () => {
    let isErr = false;
    const errs = {
      name: "",
      roomNumber: "",
      capacity: "",
      area: "",
      description: "",
      ratePerHour: "",
    };

    if (selectedTab === "General") {
      if (!data.name || data.name === "") {
        isErr = true;
        errs.name = "Please enter space name.";
      }
      if (!data.roomNumber || data.roomNumber === "") {
        isErr = true;
        errs.roomNumber = "Please enter space room number.";
      }
      if (!data.capacity || data.capacity === "") {
        isErr = true;
        errs.capacity = "Please enter space capacity.";
      }
      if (!data.area || data.area === "") {
        isErr = true;
        errs.area = "Please enter the space area in sq ft.";
      }
      if (!data.description || data.description === "") {
        isErr = true;
        errs.description = "Please enter space description.";
      }

      if (data.description && data.description.length > maxContentLimit) {
        isErr = true;
        errs.description = `The description of the space should be within ${maxContentLimit} characters.`;
      }
    }

    if (selectedTab === "Price") {
      const rate = Number(data.ratePerHour);
      if (
        data.ratePerHour === "" ||
        data.ratePerHour === null ||
        data.ratePerHour === undefined ||
        isNaN(rate) ||
        rate <= 0
      ) {
        isErr = true;
        errs.ratePerHour = "Please enter a price greater than $0.";
      }
    }

    setErrors(errs);

    return isErr;
  };

  const saveVenueSpace = () => {
    const isErr = validate();

    if (!loading && !isErr) {
      setLoading(true);

      let spaceData;

      if (spaceId) {
        // Update Space

        spaceData = {
          id: spaceId,
          venueId: venueId,
          name: data.name,
          tagLine: data.tagLine,
          roomNumber: data.roomNumber,
          capacity: data.capacity,
          area: data.area,
          description: data.description,
          ratePerHour: data.ratePerHour,
          minHours: data.minHours,
          discountHours: data.discountHours,
          status: data.status,
        };

        updateSpaceAction(spaceData, dispatch)
          .then((res) => {
            //

            if (res.type === "success") {
              const saved = {
                ...data,
                ...res.data,
              };
              setData(saved);
              // saved → reset baseline so closing won't re-prompt
              guard.setBaseline(saved);
            }

            showToast(res.type, res.message);
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => setLoading(false));
      } else {
        // Create Space

        spaceData = {
          venueId: venueId,
          name: data.name,
          tagLine: data.tagLine,
          roomNumber: data.roomNumber,
          capacity: data.capacity,
          area: data.area,
          description: data.description,
          status: data.status,
        };

        const dataVibeTags = data.vibeTags?.map((tag: any) => tag.text) || [];
        const dataOccassionTags =
          data.occassionTags?.map((tag: any) => tag.text) || [];

        if (dataVibeTags.length > 0) {
          spaceData = { ...spaceData, vibeTags: dataVibeTags };
        }

        if (dataOccassionTags.length > 0) {
          spaceData = { ...spaceData, occassionTags: dataOccassionTags };
        }

        createSpaceAction(spaceData, dispatch)
          .then((res) => {
            //
            if (res.type === "success") {
              const spaceId = res.data.id;
              setSpaceId(spaceId);
              setSelectedTab("Features");
              // first step saved → reset baseline for the next tab's edits
              guard.setBaseline({ ...data, ...res.data });

              // Let the parent bump this venue's space count without a reload.
              if (onSpaceCreated) {
                onSpaceCreated();
              }

              // Confirm creation before the modal switches into edit mode, so
              // the user knows the space was saved. Use a clear, explicit
              // message rather than relying on the API's (which may be empty).
              showToast(
                "success",
                res.message ||
                  "Your space has been created. You can now add more details."
              );
            } else {
              showToast(res.type, res.message);
            }
          })
          .catch((err) => {
            showToast("error", err.message);
            //
          })
          .finally(() => setLoading(false));
      }
    }
  };

  const getSpaceInfos = () => {
    if (spaceItem && spaceItem.id) {
      API.getSpace(spaceItem.id)
        .then((res) => {
          if (res.type === "success") {
            setSpaceId(spaceItem.id);
            const eventType =
              res.data && res.data.tags
                ? res.data.tags.filter(
                    (item, index) => item.type === "eventType",
                  )
                : [];
            const vibeTags =
              res.data && res.data.tags
                ? res.data.tags.filter((item, index) => item.type === "vibe")
                : [];
            const occassionTags =
              res.data && res.data.tags
                ? res.data.tags.filter(
                    (item, index) => item.type === "occassion",
                  )
                : [];
            const features =
              res.data && res.data.features
                ? res.data.features.filter(
                    (item, index) => item.type === "features",
                  )
                : [];
            const amenities =
              res.data && res.data.features
                ? res.data.features.filter(
                    (item, index) => item.type === "amenities",
                  )
                : [];
            const otherTags =
              res.data && res.data.features
                ? res.data.features.filter(
                    (item, index) => item.type === "other",
                  )
                : [];

            const loaded = {
              ...res.data,
              eventType,
              vibeTags,
              occassionTags,
              features,
              amenities,
              otherTags,
              // The API returns area as a decimal string (e.g. "77.00"); strip
              // trailing zeros so the form shows "77" like the space list does.
              area:
                res.data?.area === null || res.data?.area === undefined
                  ? ""
                  : formatArea(res.data.area),
            };
            setData(loaded);
            // baseline = freshly-loaded space, so edits are detected from here
            guard.setBaseline(loaded);
          } else {
          }
        })
        .finally(() => setFetching(false));
    } else {
      setFetching(false);
    }
  };

  useEffect(() => {
    const contentLength = data.description.length;
    const remains = maxContentLimit - contentLength;

    setRemaningLimit(contentLength);
  }, [data.description]);

  useEffect(() => {
    getSpaceInfos();
  }, []);

  return (
    <Modal
      className="add-space-wrapper"
      show={show}
      onHide={guard.requestClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>{spaceId ? "Update Space" : "Add Space"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {fetching && (
          <div className="no-result-wrapper">
            <p>
              <FontAwesomeIcon icon={faSpinner} spin />
            </p>
          </div>
        )}

        {!fetching && (
          <>
            {spaceId && (
              <Tabs
                type="Small Tabs"
                tabs={tabs}
                selected={selectedTab}
                setSelected={setSelectedTab}
              />
            )}

            {selectedTab === "General" && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="required-note m-0">
                    <span className="required-star">*</span> Required
                  </p>

                  <Form.Check
                    type="switch"
                    id="space-status"
                    label={data.status === "published" ? "Publish" : "Draft"}
                    checked={data.status === "published"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange("status", "published");
                      } else {
                        onChange("status", "draft");
                      }
                    }}
                  />
                </div>
                <div className="row">
                  <div className="col-md-6 col-sm-6 col-xs-12">
                    <div className="form-group">
                      <label>
                        Space name<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Main Conference Room"
                        name="name"
                        value={data.name}
                        onChange={(e) => onChange("name", e.target.value)}
                      />
                      {errors.name && (
                        <span className="error">{errors.name}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 col-sm-6 col-xs-12">
                    <div className="form-group">
                      <label>
                        Room number<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 204 or Suite B"
                        name="roomNumber"
                        value={data.roomNumber}
                        onChange={(e) => onChange("roomNumber", e.target.value)}
                      />
                      {errors.roomNumber && (
                        <span className="error">{errors.roomNumber}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tagline</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="A short catchy line, e.g. Bright loft with city views"
                    name="tagLine"
                    value={data.tagLine}
                    onChange={(e) => onChange("tagLine", e.target.value)}
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 col-sm-6 col-xs-12">
                    <div className="form-group">
                      <label>
                        Capacity<span className="required-star">*</span>
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        placeholder="Number of guests"
                        name="capacity"
                        value={data.capacity}
                        onChange={(e) => onChange("capacity", e.target.value)}
                      />
                      {errors.capacity && (
                        <span className="error">{errors.capacity}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6 col-sm-6 col-xs-12">
                    <div className="form-group">
                      <label>
                        Property Size (Sqft)
                        <span className="required-star">*</span>
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        placeholder="Total area in sq ft"
                        name="area"
                        value={data.area}
                        onChange={(e) => onChange("area", e.target.value)}
                      />
                      {errors.area && (
                        <span className="error">{errors.area}</span>
                      )}
                    </div>
                  </div>
                </div>

                <TagsInputList
                  field="eventType"
                  type="Space"
                  systemItems={spaceTypes}
                  venueId={venueId}
                  spaceId={spaceId}
                  label="Event Type"
                  placeHolder="Click to select an event type…"
                  items={data.eventType}
                  updateTags={(items: any) => onChange("eventType", items)}
                  error={errors.eventType}
                />

                <TagsInputList
                  field="vibe"
                  type="Space"
                  systemItems={vibesTypes}
                  venueId={venueId}
                  spaceId={spaceId}
                  label="Vibes"
                  placeHolder="Click to select a vibe…"
                  items={data.vibeTags}
                  updateTags={(items: any) => onChange("vibeTags", items)}
                  error={errors.vibeTags}
                />

                <TagsInputList
                  field="occassion"
                  type="Space"
                  systemItems={occassionTypes}
                  venueId={venueId}
                  spaceId={spaceId}
                  label="Occasions"
                  placeHolder="Click to select an occasion…"
                  items={data.occassionTags}
                  updateTags={(items: any) => onChange("occassionTags", items)}
                  error={errors.occassionTags}
                />

                <div className="form-group">
                  <label>
                    Description<span className="required-star">*</span>
                  </label>

                  <textarea
                    rows={5}
                    className="form-control"
                    placeholder="Describe the space, its layout, and what makes it special"
                    name="description"
                    onChange={(e) => onChange("description", e.target.value)}
                    value={data.description}
                  />

                  <span className="remaining-content-text">
                    {remainingLimit} of {maxContentLimit}
                  </span>

                  {errors.description && (
                    <span className="error">{errors.description}</span>
                  )}
                </div>
              </>
            )}

            {selectedTab === "Features" && (
              <>
                <TagsInputList
                  field="features"
                  type="Space"
                  systemItems={featuresTypes}
                  label="Add your space features"
                  items={data.features}
                  updateTags={(items: any) => onChange("features", items)}
                  error={errors.featuresTags}
                  venueId={venueId}
                  spaceId={spaceId}
                />

                <TagsInputList
                  field="amenities"
                  type="Space"
                  systemItems={amenitiesTypes}
                  label="Add your space amenities"
                  items={data.amenities}
                  updateTags={(items: any) => onChange("amenities", items)}
                  error={errors.amenitiesTags}
                  venueId={venueId}
                  spaceId={spaceId}
                />

                <TagsInputList
                  field="other"
                  type="Space"
                  systemItems={otherTypes}
                  label="Add other tags for your space"
                  items={data.otherTags}
                  updateTags={(items: any) => onChange("otherTags", items)}
                  error={errors.otherTags}
                  venueId={venueId}
                  spaceId={spaceId}
                />
              </>
            )}

            {selectedTab === "About" && (
              <AccordianDataContent type="about" spaceId={spaceId} />
            )}

            {selectedTab === "Health & Safety" && (
              <AccordianDataContent
                type="health_and_safety"
                spaceId={spaceId}
              />
            )}

            {selectedTab === "Policy" && (
              <AccordianDataContent type="policy" spaceId={spaceId} />
            )}

            {selectedTab === "Schedule" && (
              <div className="modal-schedule-wrapper">
                <SpaceAvailability
                  spaceId={spaceId}
                  availabilities={data.availabilities}
                  onChange={(temAvailability: any) =>
                    onChange("availabilities", temAvailability)
                  }
                />
              </div>
            )}

            {selectedTab === "Price" && (
              <SpaceInfoStepFour
                space={data}
                setStepBack={() => {}}
                priceError={errors.ratePerHour}
                onChange={(field: string, value: string) =>
                  onChange(field, value)
                }
              />
            )}

            {selectedTab === "Images" && (
              <div className="p-3 ">
                <SpaceInfoStepFive
                  spaceId={spaceId}
                  media={data.media}
                  updateMedia={(newItems: Array<object>) => {
                    console.log("here for update... ");

                    // setData({ ...data, [media]: newItems })
                    onChange("media", newItems);
                  }}
                  dispatchItem={(item: object) =>
                    dispatch(updateVenueSpace({ item: item }))
                  }
                  onBack={isFirstTab ? null : goToPrevTab}
                  canSave={showActionBtn}
                  onFinish={
                    spaceItem?.id
                      ? null
                      : () => {
                          showToast(
                            "success",
                            "Your space has been set up. All done!"
                          );
                          guard.setBaseline(data);
                          closeModal();
                        }
                  }
                  finishLabel="Finish Adding Space"
                />
              </div>
            )}

            {/* Footer navigation. During initial creation (no spaceId yet)
                only the General tab is shown with its create/"Next" action.
                Once the space exists, show Back/Next to step through the tabs
                sequentially, plus Save on the editable General/Price tabs. The
                Images tab is excluded here because it renders its own inline
                Back + Save row. */}
            {spaceId === "" ? (
              showActionBtn &&
              ["General", "Price"].includes(selectedTab) && (
                <div className="form-group mb-0 mt-3">
                  <CustomButton
                    isLoading={loading}
                    title="Next"
                    onClick={saveVenueSpace}
                  />
                </div>
              )
            ) : selectedTab === "Images" ? null : (
              <div className="space-tab-nav form-group mb-0 mt-3 d-flex justify-content-between align-items-center">
                <div>
                  {!isFirstTab && (
                    <CustomButton
                      classList="btn-secondary"
                      title="Back"
                      onClick={goToPrevTab}
                    />
                  )}
                </div>

                <div className="d-flex gap-2">
                  {showActionBtn &&
                    ["General", "Price"].includes(selectedTab) && (
                      <CustomButton
                        isLoading={loading}
                        title="Save"
                        onClick={saveVenueSpace}
                      />
                    )}

                  {!isLastTab && (
                    <CustomButton title="Next" onClick={goToNextTab} />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {guard.showConfirm && (
        <ConfirmationModal
          show={guard.showConfirm}
          heading="Discard changes?"
          message="Are you sure you want to close? You may lose your changes."
          closeModal={guard.cancelClose}
          buttons={[
            {
              text: "Keep editing",
              variant: "outline-dark",
              className: "confirmation-cancel-btn",
              action: guard.cancelClose,
            },
            {
              text: "Discard Changes",
              action: guard.confirmDiscard,
            },
          ]}
        />
      )}
    </Modal>
  );
};

export default AddVenueSpace;
